<script lang="ts">
  import { untrack } from 'svelte';
  import Icon from './Icon.svelte';
  import { autocompletePlaces, newSessionToken, textSearch } from './api';
  import { formatTypeLabel } from '~/shared/place-type.js';
  import { formatDistanceFrom } from '~/shared/group-actions.js';
  import type {
    AutocompleteSuggestion,
    PoiView,
    TextSearchResult,
    TripCityView,
    GroupView,
  } from '~/shared/api.js';

  let {
    tripToken,
    editable,
    pois,
    activeCity = null,
    hasCities = false,
    autoAddGroup = null,
    discoveryMode = false,
    discoveryDirty = false,
    query = $bindable(''),
    onPickPlace,
    onJumpToPoi,
    onAddCity,
    onDiscoverHere,
    onImportUrl,
  }: {
    tripToken: string;
    editable: boolean;
    pois: PoiView[];
    activeCity?: TripCityView | null;
    /** True when the trip has *any* cities (whether or not one is active).
     *  Controls the "add a city" nudge — we shouldn't suggest adding a city
     *  when the user has already added some, even if none is currently the
     *  search focus. */
    hasCities?: boolean;
    autoAddGroup?: GroupView | null;
    /** Map-view discovery mode: typing does not fire autocomplete, the
     *  dropdown is suppressed, and the inline "this area" pill replaces
     *  Enter as the commit gesture. */
    discoveryMode?: boolean;
    /** Pill should blink — there's an uncommitted change (query or
     *  viewport differs from the last text-search). Only meaningful
     *  while discoveryMode. */
    discoveryDirty?: boolean;
    /** Two-way bound: parent reads the user's query to compute pill state. */
    query?: string;
    /** Fired when the user picks an autocomplete suggestion or text-search
     *  result. The parent runs preview → bookmark using the same session
     *  token so Google bills autocomplete + details as one session. */
    onPickPlace: (placeId: string, sessionToken: string, displayName: string) => void;
    onJumpToPoi: (placeId: string) => void;
    onAddCity?: () => void;
    /** Tapped the inline "this area" pill — commit a discovery search. */
    onDiscoverHere?: () => void;
    /** Tapped the inline "From URL" pill — open the URL-import modal.
     *  When provided, a pill is rendered as the trailing element of the
     *  search input (list view only — hidden in discovery mode). */
    onImportUrl?: () => void;
  } = $props();
  let suggestions: AutocompleteSuggestion[] = $state([]);
  let open = $state(false);
  // We delay setting `open = false` on blur so a click on a dropdown
  // row registers before the dropdown unmounts. Store the timer so a
  // re-focus (e.g. parent calling searchInput.focus() after a sheet
  // closes) can cancel it — otherwise the pending blur timeout would
  // hide the dropdown ~150ms after we just brought it back.
  let blurTimer: ReturnType<typeof setTimeout> | null = null;
  function openDropdown() {
    if (blurTimer !== null) {
      clearTimeout(blurTimer);
      blurTimer = null;
    }
    open = true;
  }
  function scheduleClose() {
    blurTimer = setTimeout(() => {
      open = false;
      blurTimer = null;
    }, 150);
  }
  let busy = $state(false);
  let error: string | null = $state(null);
  let sessionToken = $state(newSessionToken());
  let mode: 'autocomplete' | 'textsearch' = $state('autocomplete');
  let textResults: TextSearchResult[] = $state([]);
  let textSearching = $state(false);
  let inputEl: HTMLInputElement | null = $state(null);

  export function focus() {
    inputEl?.focus();
  }

  /** Parent calls this after a successful bookmark so the next search
   *  starts a fresh Places session. */
  export function rotateSession(): void {
    sessionToken = newSessionToken();
  }

  /** Parent calls this after a successful bookmark to clear the query and
   *  collapse the dropdown — moved out of pickSuggestion since the parent
   *  now controls the bookmark commit. */
  export function clearQuery(): void {
    query = '';
    suggestions = [];
    textResults = [];
    mode = 'autocomplete';
  }

  const AUTOCOMPLETE_CAP = 5;

  $effect(() => {
    const q = query.trim();
    // Track the active city so the dropdown re-fetches when it changes
    // (e.g., user adds a city via the dropdown tip while results are visible).
    void activeCity?.id;
    // Reset textsearch state when the query changes. Untracked so this
    // effect doesn't re-run (and undo the work) when findMore() flips
    // mode to 'textsearch' or assigns to textResults.
    untrack(() => {
      if (mode !== 'autocomplete') mode = 'autocomplete';
      if (textResults.length > 0) textResults = [];
    });
    // In map view, the map IS the result surface. Skip the autocomplete
    // API call entirely and clear any prior suggestions so the dropdown
    // stays hidden.
    if (discoveryMode) {
      if (suggestions.length > 0) suggestions = [];
      return;
    }
    if (!q) {
      if (suggestions.length > 0) suggestions = [];
      return;
    }
    const controller = new AbortController();
    const handle = setTimeout(async () => {
      try {
        if (editable) {
          const remote = await autocompletePlaces(tripToken, q, sessionToken, {
            signal: controller.signal,
          });
          suggestions = remote;
        } else {
          const lower = q.toLowerCase();
          suggestions = pois
            .filter((p) => (p.customName ?? p.name).toLowerCase().includes(lower))
            .map((p) => ({
              placeId: p.placeId,
              primary: p.customName ?? p.name,
              secondary: '',
              alreadyBookmarked: true,
              category: p.category,
              distanceMeters: null,
            }));
        }
        error = null;
      } catch (e) {
        if (isAbortError(e)) return;
        error = e instanceof Error ? e.message : 'Search failed';
        suggestions = [];
      }
    }, 250);
    return () => {
      clearTimeout(handle);
      controller.abort();
    };
  });

  function isAbortError(e: unknown): boolean {
    return e instanceof DOMException && e.name === 'AbortError';
  }

  function pickSuggestion(s: AutocompleteSuggestion) {
    if (s.alreadyBookmarked) {
      onJumpToPoi(s.placeId);
      query = '';
      return;
    }
    if (!editable) return;
    // Hand off to the parent so it can fetch the preview, show the sheet,
    // and commit the bookmark on confirmation. SearchInput stops here.
    onPickPlace(s.placeId, sessionToken, s.primary);
  }

  async function findMore() {
    const q = query.trim();
    if (!q || textSearching) return;
    textSearching = true;
    error = null;
    try {
      textResults = await textSearch(tripToken, q);
      mode = 'textsearch';
    } catch (e) {
      if (!isAbortError(e)) {
        error = e instanceof Error ? e.message : 'Search failed';
      }
    } finally {
      textSearching = false;
    }
  }

  function pickTextResult(r: TextSearchResult) {
    if (r.alreadyBookmarked) {
      onJumpToPoi(r.placeId);
      query = '';
      return;
    }
    if (!editable) return;
    onPickPlace(r.placeId, sessionToken, r.name);
  }

  function backToAutocomplete() {
    mode = 'autocomplete';
    textResults = [];
  }

  const placeholder = $derived.by(() => {
    if (!editable) return 'Find a place in this trip…';
    // Map view always searches the current viewport, so the active-city
    // hint is misleading there. List view still uses autocomplete with
    // a city bias, so the city name is meaningful.
    if (discoveryMode) return 'Search this area…';
    if (activeCity) return `Search places in ${activeCity.name}…`;
    return 'Search places to bookmark…';
  });

  const trimmed = $derived(query.trim());
  const hasQuery = $derived(trimmed !== '');
  // No dropdown in map view — the map is the result surface.
  const showDropdown = $derived(open && hasQuery && !discoveryMode);
  const showEmpty = $derived(showDropdown && suggestions.length === 0 && !error);
  // Pill is visible whenever there's a query to commit. Without a query
  // there's nothing to search, and a static, never-active pill is just
  // noise next to the input.
  const showPill = $derived(discoveryMode && hasQuery);
  // Import-URL pill is the list-view counterpart to the area pill —
  // mutually exclusive (map view gets area, list view gets URL).
  // Always visible when wired up; an "alternate input mode" affordance.
  const showImportPill = $derived(!discoveryMode && onImportUrl !== undefined);
</script>

<div class="relative">
  <div class="search-pill" class:search-pill--with-trail={showPill || showImportPill}>
    <Icon name="search" size={16} strokeWidth={2.25} />
    <input
      bind:this={inputEl}
      type="text"
      bind:value={query}
      placeholder={placeholder}
      aria-label={placeholder}
      onfocus={openDropdown}
      onblur={scheduleClose}
      onkeydown={(e) => {
        // In map view, Enter commits the discovery search — same as
        // clicking the blinking pill. Outside discovery mode, Enter
        // falls through to the browser default (no-op for type="text"
        // outside a form).
        if (e.key === 'Enter' && discoveryMode && hasQuery) {
          e.preventDefault();
          onDiscoverHere?.();
        }
      }}
    />
    {#if busy}
      <span class="text-caption-sm" style="color: var(--color-muted);">…</span>
    {/if}
    {#if hasQuery}
      <button
        type="button"
        class="clear-btn"
        aria-label="Clear search"
        title="Clear search"
        onclick={() => (query = '')}
        onmousedown={(e) => e.preventDefault()}
      >
        <Icon name="x" size={14} strokeWidth={2.5} />
      </button>
    {/if}
    {#if showPill}
      <button
        type="button"
        class="discover-pill"
        class:discover-pill--dirty={discoveryDirty}
        aria-label="Search this area"
        title="Search this area"
        onclick={() => onDiscoverHere?.()}
        onmousedown={(e) => e.preventDefault()}
      >
        <Icon name="search" size={13} strokeWidth={2.25} />
        <span>this area</span>
      </button>
    {/if}
    {#if showImportPill}
      <button
        type="button"
        class="import-pill"
        aria-label="Add a place from a URL"
        title="Add a place from a URL"
        onclick={() => onImportUrl?.()}
        onmousedown={(e) => e.preventDefault()}
      >
        <Icon name="plus" size={13} strokeWidth={2.25} />
        <span>From URL</span>
      </button>
    {/if}
  </div>

  {#if showDropdown}
    <div class="dropdown">
      {#if editable && !hasCities && mode === 'autocomplete'}
        <div class="tip">
          <Icon name="map-pin" size={13} />
          <span class="tip-text">
            Add a city to your trip — search will focus on places there.
          </span>
          {#if onAddCity}
            <button
              type="button"
              class="tip-link"
              onmousedown={(e) => e.preventDefault()}
              onclick={() => onAddCity?.()}
            >Add a city</button>
          {/if}
        </div>
      {/if}
      {#if editable && autoAddGroup && mode === 'autocomplete'}
        <div class="tip tip-group">
          <span class="dot dot--{autoAddGroup.tint}"></span>
          <span class="tip-text">
            Bookmarks will join the <strong>{autoAddGroup.name}</strong> group.
          </span>
        </div>
      {/if}
      {#if error}
        <div class="empty">{error}</div>
      {:else if mode === 'textsearch'}
        <button
          type="button"
          class="back-btn"
          onmousedown={(e) => e.preventDefault()}
          onclick={backToAutocomplete}
        >
          <Icon name="arrow-up-right" size={13} />
          <span>Back to quick suggestions</span>
        </button>
        {#if textResults.length === 0}
          <div class="empty">No places match "{trimmed}".</div>
        {:else}
          <ul>
            {#each textResults as r (r.placeId)}
              <li>
                <button
                  type="button"
                  class="row"
                  onmousedown={(e) => e.preventDefault()}
                  onclick={() => pickTextResult(r)}
                >
                  <span class="row-text">
                    <span class="row-primary">{r.name}</span>
                    {#if r.formattedAddress}
                      <span class="row-address">{r.formattedAddress}</span>
                    {/if}
                  </span>
                  {#if r.alreadyBookmarked}
                    <span class="row-tag">✓ Bookmarked</span>
                  {/if}
                </button>
              </li>
            {/each}
          </ul>
        {/if}
      {:else if showEmpty}
        <div class="empty">
          {editable
            ? `No places matching "${trimmed}". Try a different spelling.`
            : `No places in this trip matching "${trimmed}".`}
        </div>
      {:else}
        <ul>
          {#each suggestions as s (s.placeId)}
            {@const typeLabel = formatTypeLabel(s.category)}
            {@const distance =
              s.distanceMeters !== null && activeCity
                ? formatDistanceFrom(s.distanceMeters, activeCity.name)
                : null}
            <li>
              <button
                type="button"
                class="row"
                onmousedown={(e) => e.preventDefault()}
                onclick={() => pickSuggestion(s)}
              >
                <span class="row-text">
                  <span class="row-primary">{s.primary}</span>
                  {#if typeLabel || s.secondary || distance}
                    <span class="row-secondary">
                      {#if typeLabel}<span class="row-pill">{typeLabel}</span>{/if}
                      {#if s.secondary}<span class="row-meta">{s.secondary}</span>{/if}
                      {#if distance}<span class="row-meta">· {distance}</span>{/if}
                    </span>
                  {/if}
                </span>
                {#if s.alreadyBookmarked}
                  <span class="row-tag">✓ Bookmarked</span>
                {/if}
              </button>
            </li>
          {/each}
        </ul>
        {#if editable && suggestions.length >= AUTOCOMPLETE_CAP}
          <button
            type="button"
            class="more-btn"
            onmousedown={(e) => e.preventDefault()}
            onclick={findMore}
            disabled={textSearching}
          >
            <Icon name="search" size={13} />
            <span>{textSearching ? 'Finding more…' : 'Find more results'}</span>
          </button>
        {/if}
      {/if}
    </div>
  {/if}
</div>

<style>
  /* Cut the search pill's right padding when the inline "this area"
     pill is rendered, so it sits flush with the edge. */
  :global(.search-pill--with-trail) { padding-right: 4px; gap: 8px; }

  /* Inline "Search this area" pill. Always rendered when the user has
     a query in map view (showPill); blinks when there's an
     uncommitted change to fire (discoveryDirty). Dashed border
     matches the "computed slice" visual language used by locality
     auto-city chips. */
  .discover-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    flex: none;
    height: 36px;
    padding: 0 12px;
    background: var(--color-canvas);
    color: var(--color-ink);
    border: 1px dashed var(--color-border-strong);
    border-radius: 9999px;
    cursor: pointer;
    font: inherit;
    font-size: 13px;
    font-weight: 500;
    transition: background 100ms ease, color 100ms ease, border-color 100ms ease;
  }
  .discover-pill:hover { background: var(--color-surface-soft); }

  /* List-view counterpart: opens the URL-import modal. Same dashed
     pill chrome as .discover-pill so the two trailing affordances
     read as siblings across views (map ↔ list). No dirty state — this
     pill doesn't have a queued action; it just opens a modal. */
  .import-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    flex: none;
    height: 36px;
    padding: 0 12px;
    background: var(--color-canvas);
    color: var(--color-ink);
    border: 1px dashed var(--color-border-strong);
    border-radius: 9999px;
    cursor: pointer;
    font: inherit;
    font-size: 13px;
    font-weight: 500;
    transition: background 100ms ease, border-color 100ms ease;
  }
  .import-pill:hover { background: var(--color-surface-soft); }
  /* Dirty state: primary-colored fill + expanding sonar ring signals
     "there's something to commit — tap me." Primary (not ink) reads
     as a call to action rather than a current state. */
  .discover-pill--dirty {
    background: var(--color-primary);
    color: var(--color-on-primary);
    border-color: var(--color-primary);
    border-style: solid;
    animation: discover-pulse 1.6s ease-out infinite;
  }
  .discover-pill--dirty:hover { background: var(--color-primary-active); }
  @keyframes discover-pulse {
    0% {
      box-shadow: 0 0 0 0 color-mix(in srgb, var(--color-primary) 55%, transparent);
    }
    100% {
      box-shadow: 0 0 0 10px color-mix(in srgb, var(--color-primary) 0%, transparent);
    }
  }
  /* Respect reduced-motion preferences — keep the color change but
     drop the sonar ring. */
  @media (prefers-reduced-motion: reduce) {
    .discover-pill--dirty { animation: none; }
  }

  /* Inline clear button — appears whenever the input is non-empty so
     users can dismiss the query in one tap instead of backspacing. */
  .clear-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: none;
    width: 28px;
    height: 28px;
    padding: 0;
    background: var(--color-surface-soft);
    color: var(--color-muted);
    border: 0;
    border-radius: 9999px;
    cursor: pointer;
    transition: background 100ms ease, color 100ms ease;
  }
  .clear-btn:hover {
    background: var(--color-surface-strong);
    color: var(--color-ink);
  }

  .dropdown {
    position: absolute;
    left: 0;
    right: 0;
    margin-top: 0.5rem;
    z-index: 20;
    background: var(--color-canvas);
    border: 1px solid var(--color-hairline);
    border-radius: var(--radius-md);
    box-shadow:
      rgba(0, 0, 0, 0.02) 0 0 0 1px,
      rgba(0, 0, 0, 0.04) 0 2px 6px 0,
      rgba(0, 0, 0, 0.1) 0 4px 8px 0;
    overflow: hidden;
  }
  .empty {
    padding: 0.875rem 1rem;
    font-size: 14px;
    line-height: 1.43;
    color: var(--color-muted);
  }
  ul {
    margin: 0;
    padding: 0;
    list-style: none;
  }
  li + li .row {
    border-top: 1px solid var(--color-hairline-soft);
  }
  .row {
    width: 100%;
    text-align: left;
    padding: 0.75rem 1rem;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.75rem;
    background: transparent;
    min-height: 44px;
    border: 0;
    cursor: pointer;
    transition: background-color 100ms ease;
  }
  .row:hover,
  .row:focus-visible {
    background: var(--color-surface-soft);
    outline: none;
  }
  .row:active {
    background: var(--color-surface-strong);
  }
  .row-text {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    min-width: 0;
  }
  .row-primary {
    font-size: 16px;
    font-weight: 500;
    color: var(--color-ink);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .row-secondary {
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.375rem;
    font-size: 13px;
    color: var(--color-muted);
    overflow: hidden;
  }
  .row-pill {
    display: inline-block;
    padding: 1px 8px;
    background: var(--color-surface-soft);
    color: var(--color-ink);
    border-radius: 9999px;
    font-size: 11px;
    font-weight: 500;
    line-height: 1.5;
  }
  .row-meta { color: var(--color-muted); }
  .row-tag {
    font-size: 13px;
    color: var(--color-muted);
    flex-shrink: 0;
    margin-top: 0.125rem;
  }
  .row-address {
    font-size: 13px;
    color: var(--color-muted);
    line-height: 1.35;
    word-break: break-word;
  }
  .more-btn,
  .back-btn {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    background: var(--color-surface-soft);
    border: 0;
    border-top: 1px solid var(--color-hairline-soft);
    padding: 0.625rem 1rem;
    font-size: 13px;
    font-weight: 500;
    color: var(--color-ink);
    cursor: pointer;
    text-align: left;
    font-family: inherit;
    min-height: 40px;
  }
  .more-btn:hover:not(:disabled),
  .back-btn:hover {
    background: var(--color-surface-strong);
  }
  .more-btn:disabled { opacity: 0.6; cursor: wait; }
  .back-btn {
    border-top: 0;
    border-bottom: 1px solid var(--color-hairline-soft);
    background: var(--color-canvas);
    color: var(--color-muted);
  }
  .tip {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.875rem;
    background: var(--color-primary-soft);
    color: var(--color-ink);
    border-bottom: 1px solid color-mix(in srgb, var(--color-primary) 25%, transparent);
    font-size: 12px;
    line-height: 1.4;
  }
  .tip-text { flex: 1; min-width: 0; }
  .tip-link {
    background: transparent;
    border: 0;
    padding: 0.25rem 0.5rem;
    color: var(--color-ink);
    font: inherit;
    font-weight: 500;
    text-decoration: underline;
    text-underline-offset: 2px;
    cursor: pointer;
    flex-shrink: 0;
  }
  .tip-link:hover { color: var(--color-primary-active); }
  .tip-group {
    background: var(--color-surface-soft);
    border-bottom: 1px solid var(--color-hairline-soft);
    color: var(--color-ink);
  }
  .tip-group .dot { width: 8px; height: 8px; border-radius: 9999px; flex-shrink: 0; }
</style>
